import { JsonInput, Modal } from '@mantine/core';
import {
  useClipboard,
  useDebouncedValue,
  useDisclosure,
  useFullscreenElement,
  useMergedRef,
  useResizeObserver,
  useThrottledValue,
} from '@mantine/hooks';
import { IconArrowsMaximize, IconArrowsMinimize, IconCheck, IconCode, IconCopy } from '@tabler/icons-react';
import cx from 'clsx';
import { type ReactNode, use } from 'react';

import { DownloadButton } from '#core/components/shared/DownloadButton.tsx';
import { IconActionButton } from '#core/components/shared/IconActionButton.tsx';
import { useRegisterTileExport } from '#core/contexts/DashboardExportContext.ts';
import { ProjectContext } from '#core/contexts/ProjectContext.ts';

import classes from './TileFrame.module.css';

export interface TileFrameProps {
  /** Stable identity for the export registry. */
  readonly id: string;
  /** Human-readable file-name slug; used for the CSV download and the ZIP entry. */
  readonly name: string;
  /** Omit for tiles with no downloadable data (e.g. metric/KPI). */
  readonly getCsv?: () => string;
  /** Dev-only "view raw data" payload — gated on `config.debug.devtools.enabled`. */
  readonly inspect?: () => unknown;
  readonly children: ReactNode;
}

/**
 * Per-tile chrome: hosts the tile-level actions overlay (download CSV,
 * copy CSV, fullscreen, [dev] inspect raw data), and registers the tile's
 * CSV producer with the enclosing dashboard's export registry. Pauses
 * paint+layout of the tile's contents while the user is actively resizing
 * (debounced ResizeObserver → `.is-resizing` class → CSS
 * `content-visibility: hidden` on `.tile-content`), and skips paint+layout
 * while scrolled off-screen (`.is-measured` class → `content-visibility:
 * auto` on `.tile-frame`). The off-screen skip is gated on `.is-measured`
 * so the natural first paint happens before CV-auto can skip the tile;
 * the same gate enables an inline `contain-intrinsic-size` driven by
 * `debouncedRect`, supplying CV-auto with an explicit, React-tracked box
 * size while the tile is skipped. We don't rely on the browser's
 * `contain-intrinsic-size: auto` auto-remember because in practice it
 * wasn't capturing the right size through the panels' Suspense + data-
 * load lifecycle, manifesting as collapse to a fallback on scroll-out.
 * Resize, measurement, and fullscreen state are exposed to CSS via the
 * `.is-resizing`, `.is-measured`, and `.is-fullscreen` classes on
 * `.tile-frame` (see `TileFrame.module.css`); state classes rather than
 * typed custom properties with `@container style()` queries because the
 * latter aren't supported on Firefox. The CSS-driven gate replaces an
 * earlier `<Activity>`-based one because React-level unmount removed the
 * panel's own intrinsic-size scaffolding, which (combined with
 * `grid-auto-rows: auto`) collapsed grid rows to 0 and created a
 * self-sustaining feedback loop with the ResizeObserver.
 */
export function TileFrame({ id, name, getCsv, inspect, children }: TileFrameProps) {
  const [sizeRef, rect] = useResizeObserver<HTMLDivElement>();
  const [debouncedRect] = useDebouncedValue(rect, 50);
  // First-paint guard. `isMeasured` flips true once the debounced rect
  // has captured at least one real measurement. It gates two things:
  //   1. The `.is-measured` class — which is what actually turns on
  //      `content-visibility: auto`. Without this gate, off-screen tiles
  //      would be CV-skipped on first paint *before* any natural render
  //      has happened, so `contain-intrinsic-size: auto 320px` would
  //      report 320px (the never-rendered fallback) instead of the
  //      tile's real size. Result: a visible "jump" from 320px to the
  //      natural height the first time the tile scrolls into view.
  //   2. `isResizing` — so the initial 0→size rect transition doesn't
  //      momentarily flip `.is-resizing` on right after first paint
  //      (which would briefly CV-hide the content that just rendered).
  const isMeasured = debouncedRect.width > 0 && debouncedRect.height > 0;
  const isResizing = useThrottledValue(
    isMeasured && (rect.width !== debouncedRect.width || rect.height !== debouncedRect.height),
    200,
  );
  const { ref: fullscreenRef, fullscreen, toggle } = useFullscreenElement<HTMLDivElement>();
  const rootRef = useMergedRef(sizeRef, fullscreenRef);

  useRegisterTileExport(getCsv === undefined ? null : { id, name, getCsv });

  // Explicit intrinsic size driven by the measured rect, used as the
  // box dimensions while `content-visibility: auto` is skipping the
  // tile off-screen. Replaces a previous reliance on the browser's
  // `contain-intrinsic-size: auto` auto-remember, which in practice
  // wasn't capturing the right size through our render lifecycle
  // (Suspense skeleton → real chart → CV-auto activation) and showed
  // up as a collapse to the 320px fallback on scroll-out. With an
  // explicit React-driven value we get a deterministic size that
  // tracks `debouncedRect` across resizes and data-driven re-layouts.
  // Only set once `isMeasured` is true — until then CV-auto is also
  // off (same gate), so no intrinsic size is needed.
  const intrinsicSizeStyle = isMeasured
    ? { containIntrinsicSize: `${debouncedRect.width}px ${debouncedRect.height}px` }
    : undefined;

  // State classes drive resize-pause, off-screen CV-skip, and
  // fullscreen-actions rules in TileFrame.module.css. They replace an
  // earlier `@container style()` approach because Firefox's latest still
  // doesn't ship CSS style queries — class-based selectors work
  // cross-browser.
  return (
    <div
      className={cx(
        classes.tileFrame,
        isMeasured && classes.isMeasured,
        isResizing && classes.isResizing,
        fullscreen && classes.isFullscreen,
      )}
      ref={rootRef}
      style={intrinsicSizeStyle}
    >
      <div className={classes.tileContent}>{children}</div>
      <TileActions fullscreen={fullscreen} getCsv={getCsv} inspect={inspect} name={name} toggleFullscreen={toggle} />
    </div>
  );
}

interface TileActionsProps {
  readonly fullscreen: boolean;
  readonly getCsv: (() => string) | undefined;
  readonly inspect: (() => unknown) | undefined;
  readonly name: string;
  readonly toggleFullscreen: () => Promise<void>;
}

function TileActions({ fullscreen, getCsv, inspect, name, toggleFullscreen }: TileActionsProps) {
  // `ProjectContext`'s default value is a typed-but-empty stub
  // (`{} as ProjectContextProps`) for components rendered outside a
  // `ProjectProvider` — e.g. standalone storybook stories or tests that
  // bypass the app shell. Treat a missing/empty config as "devtools off"
  // so the inspect action only ever surfaces in real dev mode.
  const { config } = use(ProjectContext);
  const showInspect = Boolean(config?.debug?.devtools?.enabled) && inspect !== undefined;
  const [inspectOpen, { open, close }] = useDisclosure(false);
  const clipboard = useClipboard({ timeout: 1500 });
  const FullscreenIcon = fullscreen ? IconArrowsMinimize : IconArrowsMaximize;
  const fullscreenLabel = fullscreen ? 'Exit fullscreen' : 'Fullscreen';

  return (
    <div className={classes.tileActions}>
      {getCsv !== undefined && (
        <>
          <DownloadButton
            getData={getCsv}
            getFileName={() => `${name}.csv`}
            mimeType="text/csv"
            size="sm"
            tooltip="Download CSV"
            variant="subtle"
          />
          <IconActionButton
            ariaLabel="Copy CSV"
            label={clipboard.copied ? 'Copied' : 'Copy CSV'}
            onClick={() => clipboard.copy(getCsv())}
            size="sm"
            variant="subtle"
          >
            {clipboard.copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
          </IconActionButton>
        </>
      )}
      <IconActionButton label={fullscreenLabel} onClick={toggleFullscreen} size="sm" variant="subtle">
        <FullscreenIcon size={14} />
      </IconActionButton>
      {showInspect && (
        <>
          <IconActionButton label="View raw data" onClick={open} size="sm" variant="subtle">
            <IconCode size={14} />
          </IconActionButton>
          <Modal onClose={close} opened={inspectOpen} size="lg" title={`Raw data — ${name}`}>
            <JsonInput autosize maxRows={30} minRows={10} readOnly value={JSON.stringify(inspect?.(), null, 2)} />
          </Modal>
        </>
      )}
    </div>
  );
}
